// eslint-disable camelcase
import { useApolloClient } from '@apollo/client'
import gql from 'graphql-tag'
import Axios from 'axios'
import Cookie from 'js-cookie'
import { CachesRegistry } from 'lib/cache'
import { TokenStorage } from 'lib/TokenStorage'
import localforage from 'localforage'
import { useEffect, useState } from 'react'
import { createContainer } from 'unstated-next'
import { clearCache, useApolloState } from 'lib/apollo/Provider'
import { Analytics } from 'modules/analytics/constants'
import config from '../config'
import useHubspotTracking from '../hooks/useHubspotTracking'
import { colors } from '../theme'
import { AppState } from './AppState'

const initialState = {
  isAuthenticated: null,
  currentUser: null,
}

const COOKIE_SETTINGS = {
  production: { domain: '.nightzookeeper.com', secure: true, expires: 7 },
  staging: { domain: '.nightzookeeper.com', secure: true, expires: 7 },
  development: { domain: 'localhost', secure: false, expires: 7 },
}

interface IToken {
  expiresAt: Date
  value: string
}

export interface ICurrentUser {
  accountOwnerships: any[]
  tutorial?: {
    manageFeatures?: boolean
    parentReviews?: boolean
  }
  _id: string
  type: string
  countryCode: string
  name: string
  email: string
  userType: 'teacher' | 'parent' | undefined
  customPlan: boolean
  accountOrigin: string
  subscriptionEndDate: Date
  createdAt: Date
  children: {
    _id: string
    nickname: string
    username: string
    yearGroup: number
    age: number
    starTutoring?: {
      _id: string
      willCancelAt: string
      subscriptionExpiresAt: string
      status: string
    }
    registrationAge?: number | null
    birthMonth: number
    birthYear: number
    avatar: {
      url: string
    }
    countryCode: string
  }[]
  subscription: {
    id: string
    type: string
    status: string
    hasAccessToResources: boolean
    cancel_at_period_end?: boolean
    current_period_end?: number
    plan?: {
      id?: string
      currency?: string
    }
  }
}

const useCurrentUser = () => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(
    initialState.isAuthenticated
  )
  const client = useApolloClient()
  const { cacheReady } = useApolloState()
  const [loadingCurrentUser, setLoadingCurrentUser] = useState(false)
  const [currentUser, setCurrentUser] = useState<ICurrentUser | null>(
    initialState.currentUser
  )
  const [primary, setPrimary] = useState(colors.primary)
  const { resetState } = AppState.useContainer()
  const { identify } = useHubspotTracking()
  const [signUp, setSignUp] = useState(false)

  useEffect(() => {
    if (
      typeof window !== 'undefined' &&
      // @ts-ignore
      typeof window.profitwell === 'function' &&
      currentUser?._id
    ) {
      // @ts-ignore
      window.profitwell('start', { user_email: currentUser.email })
    }
  }, [currentUser])

  const storeToken = async ({
    user,
    accessToken,
    accessTokenExpiresAt,
    refreshToken,
    refreshTokenExpiresAt,
  }) => {
    // NZK.com adapter
    if (user && user.type === 'parent') {
      localStorage.setItem('lscache-v2-user-id', user._id)
      localStorage.setItem('lscache-v2-shared-secret-key', user.sharedSecretKey)
      localStorage.setItem('lscache-v2-user-type', user.type)

      await TokenStorage.setItem('token', {
        value: accessToken,
        expiresAt: accessTokenExpiresAt,
      })
    }

    Cookie.set(
      'nzk-edu-token',
      JSON.stringify({
        user: {
          _id: user._id,
          type: user.type,
          email: user.email,
          sharedSecretKey: user.sharedSecretKey,
          username: user.username,
        },
        accessToken: {
          value: accessToken,
          expiresAt: accessTokenExpiresAt,
        },
        refreshToken: {
          value: refreshToken,
          expiresAt: refreshTokenExpiresAt,
        },
      }),
      COOKIE_SETTINGS[config.env] || {}
    )
    return true
  }

  const tryCookieAuth = async () => {
    let cookie = Cookie.get('nzk-edu-token')
    if (!cookie) {
      const cookieB64 = Cookie.get('nzk_parent_token')
      if (cookieB64 && typeof window !== 'undefined' && Boolean(window.atob)) {
        const rawCookie = window.atob(cookieB64)
        try {
          const parsedCookie = JSON.parse(rawCookie)
          cookie = JSON.stringify({
            accessToken: {
              value: parsedCookie.accessToken,
              expiresAt: parsedCookie.accessTokenExpiresAt,
            },
            refreshToken: {
              value: parsedCookie.refreshToken,
              expiresAt: parsedCookie.refreshTokenExpiresAt,
            },
          })
        } catch (err) {
          //
        }
      }
    }
    if (cookie) {
      try {
        const token = JSON.parse(cookie)
        await storeToken({
          user: null,
          accessToken: token.accessToken.value,
          accessTokenExpiresAt: token.accessToken.expiresAt,
          refreshToken: token.refreshToken.value,
          refreshTokenExpiresAt: token.refreshToken.expiresAt,
        })
      } catch (err) {
        Cookie.remove('nzk-edu-token', COOKIE_SETTINGS[config.env] || {})
        Cookie.remove('nzk_parent_token', COOKIE_SETTINGS[config.env] || {})
      }
    }
  }

  const removeEduTokenCookie = () => {
    Cookie.remove('nzk-edu-token', COOKIE_SETTINGS[config.env] || {})
  }

  const logout = async () => {
    await TokenStorage.removeItem('token')
    await clearCache()
    await CachesRegistry.clear()
    // NZK.com adapter
    localStorage.removeItem('lscache-v2-user-id')
    localStorage.removeItem('lscache-v2-shared-secret-key')
    localStorage.removeItem('lscache-v2-user-type')
    removeEduTokenCookie()
    try {
      Cookie.remove('nzk_parent_token', COOKIE_SETTINGS[config.env] || {})
    } catch (err) {
      //
    }
    setCurrentUser(null)
    setIsAuthenticated(false)
    TokenStorage.removeItem('token')
    localforage.clear()
    resetState()
    if (typeof window === 'undefined') return
    window.Sentry.configureScope((scope) => {
      scope.setUser(null)
    })
  }

  const initCurrentUser = async () => {
    setLoadingCurrentUser(true)
    const { data } = await client.query({
      query: gql`
        query me {
          me {
            _id
            type
            countryCode
            name
            email
            userType
            createdAt
            hasPassword
            customPlan
            accountOrigin
            subscriptionEndDate
            tutorial {
              manageFeatures
              parentReviews
            }
            children {
              _id
              username
              nickname
              avatar {
                url
              }
              starTutoring {
                _id
                willCancelAt
                subscriptionExpiresAt
                status
              }
              yearGroup
              age
              registrationAge
              birthMonth
              birthYear
            }
            subscription {
              id
              type
              hasAccessToResources
              current_period_end
              cancel_at_period_end
              status
            }
          }
        }
      `,
      errorPolicy: 'all',
      fetchPolicy: 'network-only',
      context: { unbatched: true },
    })
    if (!data || !data.me) return

    // logout if wrong user type
    if (data.me.type !== 'parent') {
      logout()
      return
    }

    // sentry & hubspot setup
    const name = data.me.name || ''
    let firstname = name || ''
    let lastname = ''
    if (name.split(' ').length > 1) {
      firstname = name.split(' ')[0]
      lastname = name.split(' ')[1]
    }

    if (window.Sentry) {
      window.Sentry.configureScope((scope) => {
        scope.setUser({
          id: data.me._id,
          email: data.me.email,
        })
      })
    }

    identify({
      email: data.me.email,
      firstname,
      lastname,
      country: data.me.countryCode,
    })
    // amplitude
    if (typeof amplitude !== 'undefined') {
      amplitude.getInstance().setUserId(data.me._id)
      const identity = new amplitude.Identify().set('type', data.me.type)
      amplitude.getInstance().identify(identity)
    }
    // set the user
    setCurrentUser({ ...data.me })

    Analytics.initUser(data.me)
    setLoadingCurrentUser(false)
  }

  const onLoginSuccess = async (data?: any, signUp?: boolean) => {
    if (typeof window === 'undefined') return
    if (data) {
      await storeToken(data)
    }
    setSignUp(signUp || false)
    if (signUp) {
      localStorage.setItem('should-show-welcome-video', 'true')
    }
    setIsAuthenticated(true)
    await initCurrentUser()
    return true
  }

  const login = async (email, password, signUp = false) => {
    const token = await TokenStorage.getItem('token')
    if (token) return onLoginSuccess()
    if (!email || !password) return

    const params = new URLSearchParams()
    params.append('client_id', config.oauthClientId)
    params.append('grant_type', 'password')
    params.append('username', email && email.toLowerCase())
    params.append('password', password)

    try {
      const { data } = await Axios.post(
        `${config.oauthEndpoint}/oauth/token`,
        params
      )

      if (!data || data.statusCode >= 500)
        throw new Error('An unexpected has occured!')
      if (
        data &&
        data.user &&
        data.user.type !== 'parent' &&
        data.user.email !== 'ben@nightzookeeper.com'
      )
        throw new Error('Only parents can login here.')
      if (data && data.accessToken) return onLoginSuccess(data, signUp)
      throw new Error('Incorrect email or password.')
    } catch (err) {
      const error = err as any
      if (error.response) {
        if (
          error.response.data?.message ===
          'Invalid grant: user credentials are invalid'
        ) {
          throw new Error('Incorrect email or password.')
        }
      }
      if (error.message === 'Only parents can login here.') {
        throw new Error(error.message)
      }
      console.error(JSON.stringify(error))
      console.log('---')
      console.error(error.response)
      const loggedError = new Error(
        'An unknown error has occured. If this keeps happening, please contact support.'
      )
      // @ts-ignore
      loggedError.raw = error
      throw loggedError
    }
  }

  const init = async () => {
    await tryCookieAuth()
    const token: IToken | null = await TokenStorage.getItem('token')
    if (token && token.value && new Date(token.expiresAt) > new Date()) {
      return onLoginSuccess()
    }
    await TokenStorage.removeItem('token')
    setCurrentUser(null)
    setIsAuthenticated(false)
  }

  useEffect(() => {
    if (cacheReady) init()
  }, [cacheReady])

  const updateCurrentUser = (newUser) => {
    setCurrentUser((u) => ({ ...u, ...newUser }))
  }

  const clearTutorialStep = async (step: 'manageFeatures' | 'parentReviews') => {
    try {
      await client.mutate({
        mutation: gql`
          mutation clearTutorialStep($step: String!) {
            clearTutorialStep(step: $step)
          }
        `,
        variables: { step }
      })
      updateCurrentUser({ tutorial: { [step]: true } })
    } catch (err) {
      console.error(err)
    }
  }

  /**
   * Boolean indicating if the auth state has not been fully resolved yet.
   *
   * @example
   * true if the token has not yet been restored
   *
   * @example
   * true if the current user has not yet loaded
   */
  const authPending = isAuthenticated === null || (
    loadingCurrentUser
  )

  return {
    isAuthenticated,
    initCurrentUser,
    authPending,
    onLoginSuccess,
    currentUser,
    signUp,
    login,
    logout,
    updateCurrentUser,
    primary,
    setPrimary,
    storeToken,
    removeEduTokenCookie,
    clearTutorialStep
  }
}

export const CurrentUserState = createContainer(useCurrentUser)

export const CurrentUserStateProvider = CurrentUserState.Provider
export const useCurrentUserState = CurrentUserState.useContainer
export default CurrentUserState
